import { alpha, Box, styled, useTheme } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
// eslint-disable-next-line no-restricted-imports
import { Tooltip as MuiTooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { CustomChart, LineChart } from 'echarts/charts';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { merge } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChartApi,
  CrosshairLine,
  EChart,
  getFormattedTimeAxisLabel,
  useChartsContext,
  usePanelCrosshair,
  YAxisLabel,
} from '@/components';
import { EVENT_TIMELINE_HEIGHT, EventTimeline } from '@/components/EventTimeline';
import {
  ContentDimensions,
  EventItem,
  formatValue,
  getXAxisMinInterval,
  HeatMapYAxisOptions,
  HeatmapColorOptions,
  HeatmapLegendOptions,
  HeatmapStatLineType,
  MIN_STEP_FALLBACK_MS,
  resolveSyncGroup,
} from '@/core';
import { useAppMetadataContext } from '@/plugin-system';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { useThrottledResizing } from '../../../core/utils/resizing';
import { ColorScales, CustomColorScale, scaleToRgbString } from './color-scales';
import { HeatmapCanvasColorOptions } from './HeatMapCanvas';
import { HeatMapExplorerTooltipContent } from './HeatMapExplorerTooltipContent';
import { DEFAULT_UNIT, formatBucketBoundary, HeatMapChartData, TrimBoundMode } from './heat-map-model';
import {
  createStatLineSeries,
  drawHeatmapToCanvas,
  LOG_EPSILON,
  useHeatmapTooltip,
  useYAxisBounds,
  useYAxisZoom,
} from './utils';
import { YAxisMinimap } from './YAxisMinimap';
import { YZoomControls } from './YZoomControls';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  CustomChart,
  LineChart,
  GridComponent,
  DatasetComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  CanvasRenderer,
  VisualMapComponent,
  DataZoomComponent,
  LegendComponent,
]);

const MIN_CHART_HEIGHT = 100;
const MINIMAP_WIDTH = 40;
const MINIMAP_RIGHT_MARGIN = 5;

const PADDING = {
  right: {
    default: 10,
    legend: 46,
  },
  bottom: {
    default: 0,
    legend: 32,
    /** Horizontal stat-line legend row + gap so x-axis labels are not covered (grid is containLabel: false). */
    statLineLegend: 44,
  },
};

const EMPTY_EVENTS: EventItem[] = [];

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.popover,
    borderRadius: '4px',
    boxShadow: theme.shadows[5],
  },
}));

export interface HeatMapChartExplorerModeProps extends ContentDimensions {
  data: HeatMapChartData;
  timeZone: string;
  colorOptions: HeatmapColorOptions;
  legendOptions: HeatmapLegendOptions;
  yAxis: HeatMapYAxisOptions;
  onDataZoom: (timeRange: AbsoluteTimeRange) => void;
  customColorScale?: CustomColorScale;
  events?: EventItem[];
  onEventClick?: (event: EventItem) => void;
  panelKey?: string;
  /**
   * Override the sync group used for shared crosshair interactions.
   * By default, the sync group is derived from the app metadata context.
   * Set to a custom string to use a specific group, or `false` to opt out of syncing.
   */
  syncGroup?: string | false;
  statLines?: HeatmapStatLineType[];
  scaleType?: 'value' | 'log';
  trimRange?: {
    min_mode?: TrimBoundMode;
    max_mode?: TrimBoundMode;
    min?: number;
    max?: number;
  };
}

export function HeatMapChartExplorerMode({
  data,
  timeZone,
  width,
  height,
  colorOptions: initColorOptions,
  legendOptions,
  yAxis,
  onDataZoom,
  customColorScale,
  events = EMPTY_EVENTS,
  onEventClick,
  panelKey,
  syncGroup: syncGroupProp,
  statLines,
  scaleType = 'value',
  trimRange,
}: HeatMapChartExplorerModeProps) {
  const chartApiRef = useRef<ChartApi>();
  const chartSurfaceRef = useRef<HTMLDivElement>(null);
  const heatmapImageRef = useRef<HTMLCanvasElement | null>(null);
  const [gridRect, setGridRect] = useState<{ x: number; y: number; width: number; height: number }>();
  const prevGridRect = useRef<{ x: number; y: number; width: number; height: number }>();
  const gridRectRef = useRef(gridRect);
  gridRectRef.current = gridRect;
  const { chartsTheme } = useChartsContext();
  const { theme } = useTheme();
  const timeFormat = useUserTimeFormat();

  const explorerPanelKey = panelKey ?? 'heatmap-chart-explorer';
  const appMetadata = useAppMetadataContext();
  const resolvedSyncGroup = resolveSyncGroup(syncGroupProp, appMetadata?.app);
  const timestampToPixelX = useCallback(
    (ts: number) => {
      const pixel = chartApiRef.current?.convertToPixel('grid', [ts, 0]);
      return pixel?.[0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chartApiRef is stable; recalc when chart dimensions change
    [width, height]
  );
  const { crosshairPixelX, broadcastTimestamp, crosshairHandlers } = usePanelCrosshair({
    syncGroup: resolvedSyncGroup,
    panelKey: explorerPanelKey,
    timestampToPixelX,
  });

  const hasEvents = events.length > 0;
  const showEvents = hasEvents && gridRect && height >= MIN_CHART_HEIGHT + EVENT_TIMELINE_HEIGHT;
  const chartAreaHeight = Math.max(0, height - (showEvents ? EVENT_TIMELINE_HEIGHT : 0));

  const { throttledWidth } = useThrottledResizing({ width, height }, { throttleDimensionsMs: 100 });

  const { trimmedBounds, yScaleFactor } = useYAxisBounds({
    buckets: data.buckets,
    timeSlices: data.timeSlices,
    scaleType,
    trimRange,
  });

  const {
    visibleYRange,
    dispatchYZoom,
    handleZoomIn,
    handleZoomOut,
    handleResetYZoom,
    readAndUpdateVisibleYRange,
    yAxisDistributionEntries,
    trimmedMaxPerBucket,
    yScaleFactorRef,
  } = useYAxisZoom({ chartApiRef, gridRectRef, data, trimmedBounds, yScaleFactor, scaleType });

  const { setHoverPosition, rafRef, hoveredInfo, cellHighlight } = useHeatmapTooltip(
    chartApiRef,
    gridRect,
    data,
    trimmedBounds,
    yScaleFactor,
    statLines
  );
  const [timeDragActive, setTimeDragActive] = useState(false);
  const [timeDragPreview, setTimeDragPreview] = useState<{ left: number; width: number } | null>(null);
  const timeDragStartXRef = useRef(0);

  const hasStatLineLegend = (statLines?.length ?? 0) > 0;

  const gridOption = useMemo(() => {
    const gridLeft = yAxis.show !== false ? 60 : 10;
    const baseRight =
      legendOptions.show && legendOptions.position === 'Right' ? PADDING.right.legend : PADDING.right.default;
    let baseBottom =
      legendOptions.show && legendOptions.position === 'Bottom' ? PADDING.bottom.legend : PADDING.bottom.default;
    if (hasStatLineLegend) baseBottom += PADDING.bottom.statLineLegend;
    // Minimum grid bottom to prevent x-axis labels from being clipped:
    // 48px when the stat-line legend row is shown, 30px otherwise.
    const minBottom = hasStatLineLegend ? 48 : 30;
    return {
      left: gridLeft,
      right: baseRight + MINIMAP_WIDTH + MINIMAP_RIGHT_MARGIN + 10,
      top: 10,
      bottom: Math.max(minBottom, baseBottom),
      containLabel: false,
    };
  }, [yAxis, legendOptions.show, legendOptions.position, hasStatLineLegend]);

  const startTimeMs = data.timeRange.startMs.valueOf();
  const endTimeMs = data.timeRange.endMs.valueOf();

  const minInterval = useMemo(() => {
    const timeRangeMs = endTimeMs - startTimeMs;
    return getXAxisMinInterval(throttledWidth, timeRangeMs);
  }, [throttledWidth, startTimeMs, endTimeMs]);

  const xAxisOption = useMemo(() => {
    const rangeMs = endTimeMs - startTimeMs;
    return {
      type: 'time',
      splitLine: { show: false },
      min: startTimeMs,
      max: endTimeMs,
      minInterval,
      axisLabel: {
        hideOverlap: true,
        formatter: (value: number) => {
          return getFormattedTimeAxisLabel({ value, rangeMs, timeFormat, timeZone });
        },
      },
    };
  }, [minInterval, startTimeMs, endTimeMs, timeZone, timeFormat]);

  const unit = useMemo(() => {
    return merge({}, DEFAULT_UNIT, yAxis.unit);
  }, [yAxis.unit]);

  const yAxisOption = useMemo(() => {
    const axisMin = (scaleType === 'log' ? Math.max(LOG_EPSILON, trimmedBounds.min) : trimmedBounds.min) * yScaleFactor;
    return {
      show: yAxis.show,
      type: scaleType,
      splitLine: { show: false },
      min: axisMin,
      max: trimmedBounds.max * yScaleFactor,
      axisLabel: {
        formatter: (value: number) => {
          return formatBucketBoundary(value / yScaleFactor, unit);
        },
      },
    };
  }, [yAxis?.show, trimmedBounds, yScaleFactor, unit, scaleType]);

  const colorOptions: HeatmapCanvasColorOptions = useMemo(() => {
    if (customColorScale) {
      return { ...initColorOptions, customColorScale };
    }
    return initColorOptions;
  }, [initColorOptions, customColorScale]);

  const colorScaleMax = useMemo(
    () => colorOptions.max ?? (trimmedMaxPerBucket > 0 ? trimmedMaxPerBucket : data.maxValue),
    [colorOptions.max, trimmedMaxPerBucket, data.maxValue]
  );

  const visualMapOption = useMemo(() => {
    const min = Math.max(0, colorOptions.min ?? 0);
    const max = colorScaleMax;
    let colorScale = colorOptions.customColorScale?.colorScale ?? ColorScales[colorOptions.scale] ?? ColorScales.turbo;
    if (colorOptions.invertScale) colorScale = [...colorScale].reverse();
    if (min === max) colorScale = colorScale.slice(0, 1);

    const isBottom = legendOptions.position === 'Bottom';
    let bottomOffset: number | undefined;
    if (isBottom) {
      bottomOffset = hasStatLineLegend ? 26 : 0;
    }

    return {
      min,
      max,
      show: legendOptions.show,
      itemWidth: 10,
      orient: legendOptions.position === 'Right' ? 'vertical' : 'horizontal',
      align: legendOptions.position === 'Right' ? 'top' : 'left',
      textGap: 4,
      hoverLink: false,
      top: legendOptions.position === 'Right' ? 0 : undefined,
      right: legendOptions.position === 'Right' ? 0 : undefined,
      bottom: bottomOffset,
      left: isBottom ? 50 : undefined,
      text: [
        formatValue(max, { kind: 'Decimal', abbreviate: true }),
        formatValue(min, { kind: 'Decimal', abbreviate: true }),
      ],
      textStyle: { fontSize: 11, color: 'inherit' },
      inRange: { color: scaleToRgbString(colorScale) },
      padding: 5,
    };
  }, [colorOptions, legendOptions, colorScaleMax, hasStatLineLegend]);

  const baseStatLineSeries = useMemo(() => {
    return statLines?.length ? createStatLineSeries(data, statLines) : [];
  }, [data, statLines]);

  const statLineSeries = useMemo(() => {
    if (yScaleFactor === 1) return baseStatLineSeries;
    return baseStatLineSeries.map((s) => ({
      ...s,
      data: Array.isArray(s.data)
        ? s.data.map((point) =>
            Array.isArray(point) ? [point[0], point[1] != null ? (point[1] as number) * yScaleFactor : null] : point
          )
        : s.data,
    }));
  }, [baseStatLineSeries, yScaleFactor]);

  const heatmapCustomSeries = useMemo(() => {
    return {
      name: '__heatmap__',
      type: 'custom',
      data: [[startTimeMs, trimmedBounds.min * yScaleFactor]],
      encode: { x: 0, y: 1 },
      renderItem: (params: { coordSys: { x: number; y: number; width: number; height: number } }) => {
        const { x, y, width: gridW, height: gridH } = params.coordSys;

        const sf = yScaleFactorRef.current;
        let visYMin = trimmedBounds.min;
        let visYMax = trimmedBounds.max;
        let visXMin = startTimeMs;
        let visXMax = endTimeMs;
        if (chartApiRef.current) {
          const topLeft = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [x, y]);
          const botRight = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [x + gridW, y + gridH]);
          if (topLeft && botRight) {
            visYMax = topLeft[1]! / sf;
            visYMin = botRight[1]! / sf;
            visXMin = topLeft[0]!;
            visXMax = botRight[0]!;
          }
        }

        const canvas = heatmapImageRef.current ?? document.createElement('canvas');
        heatmapImageRef.current = canvas;
        drawHeatmapToCanvas(
          canvas,
          data,
          Math.round(gridW),
          Math.round(gridH),
          colorOptions,
          visYMin,
          visYMax,
          scaleType,
          visXMin,
          visXMax,
          colorScaleMax
        );

        return {
          type: 'image',
          style: {
            image: canvas,
            x,
            y,
            width: gridW,
            height: gridH,
          },
        };
      },
      clip: true,
      z: -1,
      silent: true,
      animation: false,
    };
  }, [
    data,
    colorOptions,
    startTimeMs,
    endTimeMs,
    trimmedBounds,
    yScaleFactor,
    scaleType,
    yScaleFactorRef,
    colorScaleMax,
  ]);

  const dataZoom = useMemo(
    () => [
      {
        id: 'x-inside',
        type: 'inside',
        xAxisIndex: 0,
        filterMode: 'none',
        preventDefaultMouseMove: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
      },
      {
        id: 'y-inside',
        type: 'inside',
        yAxisIndex: 0,
        filterMode: 'none',
        zoomLock: true,
        preventDefaultMouseMove: false,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
      },
    ],
    []
  );

  const toolbox = useMemo(
    () => ({
      feature: {
        dataZoom: {
          icon: null,
          xAxisIndex: 0,
          yAxisIndex: false,
        },
      },
    }),
    []
  );

  const noData = data.timeSlices.length === 0;

  const legendOption = useMemo(() => {
    if (!hasStatLineLegend || noData) return { show: false };
    return {
      show: true,
      bottom: 6,
      left: 56,
      orient: 'horizontal',
      textStyle: { fontSize: 11, color: 'inherit' },
      data: statLineSeries
        .filter((s) => s.name !== '__heatmap__')
        .map((s) => ({
          name: s.name,
          itemStyle: { color: (s.lineStyle as { color?: string } | undefined)?.color },
        })),
    };
  }, [hasStatLineLegend, noData, statLineSeries]);

  const option: EChartsCoreOption = useMemo(() => {
    const baseOptions = noData ? chartsTheme.noDataOption : {};

    return {
      ...baseOptions,
      animation: false,
      toolbox,
      grid: gridOption,
      xAxis: xAxisOption,
      yAxis: !noData
        ? yAxisOption
        : {
            ...yAxisOption,
            type: 'value',
            min: 0,
            max: 1,
            interval: 1,
            axisLabel: { showMaxLabel: false },
          },
      series: noData ? [] : [heatmapCustomSeries, ...statLineSeries],
      visualMap: visualMapOption,
      dataZoom: noData ? undefined : dataZoom,
      tooltip: { show: false },
      legend: legendOption,
    };
  }, [
    gridOption,
    xAxisOption,
    yAxisOption,
    visualMapOption,
    chartsTheme,
    noData,
    heatmapCustomSeries,
    statLineSeries,
    dataZoom,
    toolbox,
    legendOption,
  ]);

  // Reset x-axis dataZoom when the time range changes externally (e.g. zoom out button)
  const prevTimeRange = useRef({ start: startTimeMs, end: endTimeMs });
  useEffect(() => {
    if (prevTimeRange.current.start !== startTimeMs || prevTimeRange.current.end !== endTimeMs) {
      prevTimeRange.current = { start: startTimeMs, end: endTimeMs };
      chartApiRef.current?.dispatchAction({
        type: 'dataZoom',
        dataZoomId: 'x-inside',
        start: 0,
        end: 100,
      });
    }
  }, [startTimeMs, endTimeMs]);

  // useCallback for <EChart> props: the wrapper is memoized and useEChartEvent re-binds chart.on/off when the handler reference changes.
  const handleDatazoom = useCallback(
    (params: { batch?: unknown[] }) => {
      readAndUpdateVisibleYRange();

      const batches = (params.batch || [params]) as Array<{
        dataZoomId?: string;
        startValue?: number;
        endValue?: number;
      }>;
      for (const zoom of batches) {
        if (zoom.startValue === undefined || zoom.endValue === undefined) continue;
        if (zoom.dataZoomId === 'y-inside') continue;
        onDataZoom({
          start: new Date(zoom.startValue),
          end: new Date(zoom.endValue),
        });
        break;
      }
    },
    [onDataZoom, readAndUpdateVisibleYRange]
  );

  const handleFinished = useCallback(() => {
    if (chartApiRef.current) {
      const rect = chartApiRef.current.getChartModel().getComponent('grid').coordinateSystem.getRect();
      const prev = prevGridRect.current;
      if (!prev || prev.x !== rect.x || prev.y !== rect.y || prev.width !== rect.width || prev.height !== rect.height) {
        prevGridRect.current = rect;
        setGridRect(rect);
      }
    }

    readAndUpdateVisibleYRange();
  }, [readAndUpdateVisibleYRange]);

  // Eagerly read gridRect when the chart is already initialized but the first
  // `finished` event was missed (it can fire before useEChartEvent binds the handler).
  useEffect(() => {
    if (!gridRect && chartApiRef.current) {
      const model = chartApiRef.current.getChartModel();
      if (model) {
        const grid = model.getComponent('grid');
        if (grid?.coordinateSystem) {
          const rect = grid.coordinateSystem.getRect();
          if (rect && rect.width > 0 && rect.height > 0) {
            prevGridRect.current = rect;
            setGridRect(rect);
          }
        }
      }
      readAndUpdateVisibleYRange();
    }
  }, [gridRect, readAndUpdateVisibleYRange]);

  // Rapid mousemoves are collapsed to one hover refresh per animation frame (see rafRef guard below).
  const scheduleHoverUpdate = useCallback(
    (mouseX: number, mouseY: number) => {
      const gr = gridRectRef.current;
      if (!gr) return;
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (mouseY >= gr.y && mouseY <= gr.y + gr.height && mouseX >= gr.x && mouseX <= gr.x + gr.width) {
          if (chartApiRef.current) {
            const values = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [mouseX, mouseY]);
            const timestamp = values?.[0];
            if (timestamp !== undefined) {
              setHoverPosition({ timeMs: timestamp, x: mouseX, y: mouseY });
              broadcastTimestamp(Math.round(timestamp));
            }
          }
        } else {
          setHoverPosition(null);
          broadcastTimestamp(undefined);
        }
      });
    },
    [setHoverPosition, rafRef, broadcastTimestamp]
  );

  // Plain functions for Box native events since no EChart listener churn.
  // Identity stability with useCallback is not needed here or in the mouse handlers below.
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!chartSurfaceRef.current) return;
    const gr = gridRectRef.current;
    if (!gr) return;
    const rect = chartSurfaceRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (timeDragActive) {
      const clampedX = Math.min(gr.x + gr.width, Math.max(gr.x, mouseX));
      const startX = timeDragStartXRef.current;
      setTimeDragPreview({
        left: Math.min(startX, clampedX) - gr.x,
        width: Math.abs(clampedX - startX),
      });
      return;
    }
    scheduleHoverUpdate(mouseX, mouseY);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const gr = gridRectRef.current;
    const surface = chartSurfaceRef.current;
    if (!gr || !surface) return;
    const rect = surface.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (mouseY < gr.y || mouseY > gr.y + gr.height || mouseX < gr.x || mouseX > gr.x + gr.width) return;
    e.preventDefault();
    timeDragStartXRef.current = mouseX;
    setTimeDragActive(true);
    setTimeDragPreview({ left: mouseX - gr.x, width: 0 });
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!timeDragActive) return;
    const gr = gridRectRef.current;
    const surface = chartSurfaceRef.current;
    const api = chartApiRef.current;
    if (gr && surface && api) {
      const rect = surface.getBoundingClientRect();
      const startX = timeDragStartXRef.current;
      const endX = Math.min(gr.x + gr.width, Math.max(gr.x, e.clientX - rect.left));
      const yMid = gr.y + gr.height / 2;
      const startTime = api.convertFromPixel({ gridIndex: 0 }, [Math.min(startX, endX), yMid])?.[0];
      const endTime = api.convertFromPixel({ gridIndex: 0 }, [Math.max(startX, endX), yMid])?.[0];
      const stepMs =
        typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
          ? data.timeRange.stepMs
          : MIN_STEP_FALLBACK_MS;
      if (startTime != null && endTime != null && endTime - startTime >= stepMs) {
        onDataZoom({
          start: new Date(startTime),
          end: new Date(endTime),
        });
      }
    }
    setTimeDragActive(false);
    setTimeDragPreview(null);
  };

  const handleMouseLeave = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (timeDragActive) {
      setTimeDragActive(false);
      setTimeDragPreview(null);
    }
    crosshairHandlers.onMouseLeave();
    setHoverPosition(null);
  };

  const absoluteTimeRange: AbsoluteTimeRange = useMemo(
    () => ({
      start: new Date(data.timeRange.startMs),
      end: new Date(data.timeRange.endMs),
    }),
    [data.timeRange.startMs, data.timeRange.endMs]
  );

  return (
    <div style={{ width, height, overflow: 'auto' }}>
      {showEvents && (
        <div style={{ height: EVENT_TIMELINE_HEIGHT, width }}>
          <div style={{ paddingLeft: gridRect.x }}>
            <EventTimeline
              events={events}
              timeRange={absoluteTimeRange}
              width={gridRect.width}
              height={EVENT_TIMELINE_HEIGHT}
              onEventClick={onEventClick}
              parentPanelKey={explorerPanelKey}
            />
          </div>
        </div>
      )}
      <StyledTooltip
        title={hoveredInfo ? <HeatMapExplorerTooltipContent hoveredInfo={hoveredInfo} unit={unit} /> : ''}
        followCursor
        placement="right"
      >
        <Box
          ref={chartSurfaceRef}
          sx={{
            width,
            height: chartAreaHeight,
            position: 'relative',
            cursor: 'crosshair',
            '& canvas': { cursor: 'crosshair !important' },
          }}
          onMouseEnter={crosshairHandlers.onMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {yAxis.show && yAxis.label && (
            <div style={{ position: 'absolute', top: 0, left: 0, height: chartAreaHeight, width: '100%' }}>
              <YAxisLabel name={yAxis?.label} height={chartAreaHeight} />
            </div>
          )}
          <EChart
            key={scaleType}
            chartApiRef={chartApiRef}
            width={width}
            height={chartAreaHeight}
            option={option}
            theme={chartsTheme.echartsTheme}
            onDataZoom={handleDatazoom}
            onFinished={handleFinished}
          />

          {/* Cell highlight box */}
          {cellHighlight && hoveredInfo && (
            <div
              style={{
                position: 'absolute',
                top: cellHighlight.top,
                left: cellHighlight.left,
                width: cellHighlight.width,
                height: cellHighlight.height,
                border: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black}`,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Shared crosshair overlay */}
          {crosshairPixelX !== undefined && gridRect && (
            <CrosshairLine pixelX={crosshairPixelX} height={gridRect.height} top={gridRect.y} />
          )}

          {/* Drag-to-zoom selection overlay */}
          {gridRect && timeDragPreview && (
            <Box
              position="absolute"
              top={gridRect.y}
              left={gridRect.x + timeDragPreview.left}
              width={timeDragPreview.width}
              height={gridRect.height}
              sx={{ backgroundColor: alpha(theme.palette.common.white, 0.2), pointerEvents: 'none' }}
            />
          )}

          {/* Y zoom controls — above minimap */}
          {gridRect && !noData && (
            <YZoomControls
              left={gridRect.x + gridRect.width + MINIMAP_RIGHT_MARGIN}
              top={Math.max(0, gridRect.y - 14)}
              width={MINIMAP_WIDTH}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onReset={handleResetYZoom}
            />
          )}

          {/* Y-axis minimap */}
          {gridRect && !noData && (
            <YAxisMinimap
              entries={yAxisDistributionEntries}
              yMin={trimmedBounds.min}
              yMax={trimmedBounds.max}
              scaleType={scaleType}
              visibleYRange={visibleYRange}
              minimapLeft={gridRect.x + gridRect.width + MINIMAP_RIGHT_MARGIN}
              gridTop={gridRect.y}
              gridHeight={gridRect.height}
              minimapWidth={MINIMAP_WIDTH}
              onPan={dispatchYZoom}
              onResetZoom={handleResetYZoom}
              theme={chartsTheme.echartsTheme}
            />
          )}
        </Box>
      </StyledTooltip>
    </div>
  );
}
